import { DocumentIcon } from '@heroicons/react/24/outline';

import { Description, Field, Label } from '@oysterjs/ui/fieldset';

import type { CaraWorkspaceFileInput } from '~/api/graphql';
import { FileInput } from '~/components/file';

/**
 * Lists existing prefix or suffix PDF files for the template with a "Remove
 * File" affordance, or shows a fresh FileInput when nothing's selected. Used
 * by `HtmlEditorPage` for both Start Pages and End Pages so the visuals
 * stay aligned.
 */
export function HtmlEditorPrefixSuffixFiles(props: {
  label: 'Start Pages' | 'End Pages';
  existingFiles: { id: string; name: string }[] | null | undefined;
  reset: boolean;
  onPickFiles: (files: CaraWorkspaceFileInput[]) => void;
  onResetExisting: () => void;
  /** Drops the heading and blurb for a parent that already says what these pages are. */
  bare?: boolean;
}) {
  const showExisting = !!props.existingFiles?.length && !props.reset;
  return (
    <Field>
      {!props.bare && (
        <>
          <Label>{props.label}</Label>
          <Description>
            Upload any PDF to statically populate the{' '}
            {props.label === 'Start Pages' ? 'first' : 'last'} pages of documents generated using
            this style. This can include cover pages, disclosures, or any other static information.
          </Description>
        </>
      )}
      {showExisting && props.existingFiles && (
        <div data-slot="control" className="flex flex-row gap-4 overflow-hidden">
          {props.existingFiles.map((file) => (
            <div key={file.id} className="flex flex-row gap-2 min-w-0 overflow-hidden">
              <DocumentIcon className="size-10 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
              <div className="flex flex-col min-w-0 overflow-hidden">
                <div
                  className="text-sm text-neutral-700 dark:text-neutral-300 truncate"
                  title={file.name}
                >
                  {file.name}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    props.onPickFiles([]);
                    props.onResetExisting();
                  }}
                  className="text-left text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline cursor-pointer self-start"
                >
                  Remove File
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!showExisting && (
        <FileInput multiple onChange={(change) => props.onPickFiles(change.files)} />
      )}
    </Field>
  );
}
